import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import {
  AdminNextPrimaryKind,
  getAdminNextPrimaryTransition,
  resolveProjectStatusIds,
  showAdminApproveDecline,
} from '@app/helpers/project-state.helper';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { ToastService } from '@app/services/toast.service';
import { StepManagerService } from '@app/services/project-setup/step-manager.servive';
import { GenericStore } from '@app/stores/generic.store';
import { PersonRoleStore } from '@app/stores/person-role/person-role.store';
import { ProjectSetupStore } from '@app/stores/project-setup/project-setup.store';
import { StepsStore } from '@app/stores/project-setup/steps/steps.store';
import { WizardStore } from '@app/stores/project-setup/wizard.store';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { BaseDataStepComponent } from 'src/app/components/project-wizard/base-data-step/base-data-step.component';
import {
  getProjectWizardFormGroup,
  ProjectWizardFormGroup,
} from 'src/app/components/project-wizard/project-wizard-form.helper';
import { SectionAComponent } from 'src/app/components/project-wizard/section-a/section-a.component';
import { SectionBComponent } from 'src/app/components/project-wizard/section-b/section-b.component';
import { SectionCComponent } from 'src/app/components/project-wizard/section-c/section-c.component';
import { SectionDComponent } from 'src/app/components/project-wizard/section-d/section-d.component';
import { SectionEComponent } from 'src/app/components/project-wizard/section-e/section-e.component';
import { SectionFComponent } from 'src/app/components/project-wizard/section-f/section-f.component';
import { SectionGComponent } from 'src/app/components/project-wizard/section-g/section-g.component';
import { SectionHComponent } from 'src/app/components/project-wizard/section-h/section-h.component';
import { SectionIComponent } from 'src/app/components/project-wizard/section-i/section-i.component';
import { SectionJComponent } from 'src/app/components/project-wizard/section-j/section-j.component';
import {
  getAdminProjectIdeaFormGroup,
  type AdminProjectIdeaFormGroup,
} from '@app/components/project-admin-idea-step/admin-project-idea.form';
import { ProjectAdminIdeaStepComponent } from '@app/components/project-admin-idea-step/project-admin-idea-step.component';
import { WizardStepComponent } from 'src/app/components/wizard/wizard-step/wizard-step.component';
import {
  StepType,
  WizardComponent,
  type WizardStep,
} from 'src/app/components/wizard/wizard.component';
import {
  ProjectAdminResponse,
  ProjectBaseRequest,
} from '@app/models/admin.model';
import { StepSectionType } from '@app/stores/project-setup/steps';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize, Observable, switchMap } from 'rxjs';
import { DetailsPageHeaderComponent } from '@app/components/common/details-page-header/details-page-header.component';

@Component({
  selector: 'app-admin-project-edit',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DetailsPageHeaderComponent,
    WizardComponent,
    WizardStepComponent,
    SectionAComponent,
    SectionBComponent,
    SectionCComponent,
    SectionDComponent,
    SectionEComponent,
    SectionFComponent,
    SectionGComponent,
    SectionHComponent,
    SectionIComponent,
    SectionJComponent,
    ProjectAdminIdeaStepComponent,
    BaseDataStepComponent,
  ],
  templateUrl: './project-edit.component.html',
  styleUrls: ['./project-edit.component.scss'],
  providers: [WizardStore, StepsStore, StepManagerService],
})
export class AdminProjectEditComponent implements OnInit {
  projectSetupStore = inject(ProjectSetupStore);
  genericStore = inject(GenericProjectStore);
  genStore = inject(GenericStore);
  personRoleStore = inject(PersonRoleStore);
  protected stepsStore = inject(StepsStore);
  protected StepType = StepType;
  private readonly projectService = inject(ProjectAdminService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  private stepManagerService = inject(StepManagerService);

  private perviousBtnTranslation = 'general.previous';
  private nextBtnTranslation = 'general.next';

  protected stepIndex = signal<number>(1);
  protected prevButtonText = signal<string>(this.perviousBtnTranslation);
  protected nextButtonText = signal<string>(this.nextBtnTranslation);

  protected prevButtonHasChevron = signal<boolean>(false);
  protected nextButtonHasChevron = signal<boolean>(false);
  protected selectedProjectId = signal<string | null>(null);
  protected isAdminStepActive = signal<boolean>(true);
  protected project = signal<ProjectAdminResponse | null>(null);
  protected loadingAdminStep = signal<boolean>(false);
  protected savingAdmin = signal<boolean>(false);
  protected submittingState = signal(false);
  protected wizardInitializing = signal(false);
  protected projectName = signal<string>('');
  protected readonly adminSettingsStepComplete = signal(false);
  protected readonly projectStatusIds = computed(() =>
    resolveProjectStatusIds(this.genStore.investmentStatusEntities()),
  );

  protected readonly headerProjectName = computed(() => {
    const fromWizard = this.projectName().trim();
    if (fromWizard) {
      return fromWizard;
    }
    return this.project()?.name?.trim() ?? '';
  });

  protected readonly showAdminApproveDecline = computed(() =>
    showAdminApproveDecline(this.project()?.state, this.projectStatusIds()),
  );

  protected readonly adminNextPrimaryKind = computed(
    (): AdminNextPrimaryKind | null =>
      getAdminNextPrimaryTransition(this.project()?.state, this.projectStatusIds()),
  );

  protected readonly adminNextPrimaryLabelKey = computed(() => {
    const k = this.adminNextPrimaryKind();
    if (!k) {
      return null;
    }
    return `projectWizard.adminState.next.${k}`;
  });
  protected readonly adminStep: WizardStep<StepSectionType> = {
    title: 'projectWizard.adminForm.formTitle',
    isActive: true,
    stepType: StepType.available,
    sectionType: StepSectionType.admin,
  };
  protected readonly wizardItems = computed((): WizardStep<StepSectionType>[] => {
    const admin = {
      ...this.adminStep,
      isActive: this.isAdminStepActive(),
      stepType: this.adminSettingsStepComplete()
        ? StepType.done
        : StepType.available,
    };
    const wizardSteps = this.stepsStore
      .actualSteps()
      .map((step, index) => ({ ...step, isActive: !this.isAdminStepActive() && index === this.stepsStore.stepIndex() }));
    return [admin, ...wizardSteps];
  });
  protected adminForm: FormGroup<AdminProjectIdeaFormGroup>;

  projectWizardFormGroup: FormGroup<ProjectWizardFormGroup>;
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
  ) {
    this.projectWizardFormGroup = getProjectWizardFormGroup(fb);
    this.adminForm = getAdminProjectIdeaFormGroup(fb);
    this.selectedProjectId.set(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    this.adminForm.statusChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncAdminSettingsStepFromForm());

    const projectId = this.selectedProjectId();
    if (projectId) {
      this.loadingAdminStep.set(true);
      this.projectService.getProjectAdminById(projectId).subscribe({
        next: (project) => {
          this.project.set(project);
          this.loadingAdminStep.set(false);
          setTimeout(() => this.syncAdminSettingsStepFromForm(), 0);
        },
        error: () => {
          this.project.set(null);
          this.loadingAdminStep.set(false);
          this.adminSettingsStepComplete.set(false);
        },
      });
    }

    const id = this.selectedProjectId();
    if (id) {
      this.wizardInitializing.set(true);
      this.stepManagerService
        .initializeStepsFromData(id)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.wizardInitializing.set(false)),
        )
        .subscribe({
          next: (data) => this.projectName.set(data.projectName),
        });
    }
  }

  private syncAdminSettingsStepFromForm(): void {
    if (!this.project()) {
      this.adminSettingsStepComplete.set(false);
      return;
    }
    this.adminSettingsStepComplete.set(this.adminForm.valid);
  }

  onStepSelection(index: number) {
    if (index === 0) {
      this.isAdminStepActive.set(true);
      return;
    }

    this.isAdminStepActive.set(false);
    this.stepManagerService.onStepClick(index - 1);
  }

  protected onPreviousButtonClick(): void {
    if (this.isAdminStepActive()) {
      return;
    }

    if (this.stepsStore.stepIndex() === 0) {
      this.isAdminStepActive.set(true);
      return;
    }

    this.stepManagerService.onPreviousClick();
  }

  protected onNextButtonClick() {
    if (this.isAdminStepActive()) {
      this.saveAdminSettings(true);
      return;
    }

    this.stepManagerService.onNextClick(this.selectedProjectId()!);
  }

  goBack(): void {
    const projectId = this.selectedProjectId()?.trim();
    if (!projectId) {
      this.router.navigate(['/admin/projects']);
      return;
    }
    this.router.navigate(['/admin', 'projects', projectId]);
  }

  onSaveAdminDraft(): void {
    this.saveAdminSettings(false);
  }

  onAdminApprove(): void {
    const id = this.selectedProjectId()?.trim();
    if (!id) {
      return;
    }
    const state = this.project()?.state;
    if (state == null) {
      return;
    }
    const statusIds = this.projectStatusIds();
    this.submittingState.set(true);
    const request$ =
      state === statusIds.AnalyseStartedByStaff
        ? this.projectService.updateProjectStateToStaffAccepted(id)
        : state === statusIds.DataSupplyNeeded
        ? this.projectService.updateProjectStateToPublishable(id)
        : null;
    if (!request$) {
      this.submittingState.set(false);
      return;
    }
    request$.pipe(finalize(() => this.submittingState.set(false))).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant('projectWizard.adminState.successApprove'),
        );
        this.reloadProjectFromServer();
      },
      error: () => {
        this.toast.error(
          this.translate.instant('projectWizard.adminState.errorApprove'),
        );
      },
    });
  }

  onAdminDecline(): void {
    const id = this.selectedProjectId()?.trim();
    if (!id) {
      return;
    }
    this.submittingState.set(true);
    const state = this.project()?.state;
    if (state == null) {
      return;
    }
    const statusIds = this.projectStatusIds();
    const request$ =
      state === statusIds.AnalyseStartedByStaff
        ? this.projectService.updateProjectStateToDataSupplyNeeded(id)
        : state === statusIds.DataSupplyNeeded
        ? this.projectService.updateProjectStateToRefuse(id)
        : null;
    if (!request$) {
      this.submittingState.set(false);
      return;
    }
    request$.pipe(finalize(() => this.submittingState.set(false))).subscribe({
      next: () => {
        this.toast.success(
          this.translate.instant('projectWizard.adminState.successDecline'),
        );
        this.reloadProjectFromServer();
      },
      error: () => {
        this.toast.error(
          this.translate.instant('projectWizard.adminState.errorDecline'),
        );
      },
    });
  }

  onAdminNextPrimary(): void {
    const id = this.selectedProjectId()?.trim();
    if (!id || !this.project()) {
      this.toast.error(this.translate.instant('admin.toasts.dataLoadFailed'));
      return;
    }
    const kind = getAdminNextPrimaryTransition(
      this.project()!.state,
      this.projectStatusIds(),
    );
    if (!kind) {
      return;
    }
    const req$ = this.getAdminNextPrimaryRequest(id, kind);
    if (!req$) {
      return;
    }
    this.submittingState.set(true);
    req$
      .pipe(finalize(() => this.submittingState.set(false)))
      .subscribe({
        next: () => {
          this.toast.success(
            this.translate.instant('projectWizard.adminState.successNext'),
          );
          this.reloadProjectFromServer();
        },
        error: () => {
          this.toast.error(
            this.translate.instant('projectWizard.adminState.errorNext'),
          );
        },
      });
  }

  private getAdminNextPrimaryRequest(
    projectId: string,
    kind: AdminNextPrimaryKind,
  ): Observable<void> | null {
    const svc = this.projectService;
    switch (kind) {
      case 'staffAnalyzeStarted':
        return svc.updateProjectStateToStaffAnalyzeStarted(projectId);
      case 'staffAccepted':
        return svc.updateProjectStateToStaffAccepted(projectId);
      case 'dataSupplyNeeded':
        return svc.updateProjectStateToDataSupplyNeeded(projectId);
      case 'publishReady':
        return svc.updateProjectStateToPublishReady(projectId);
      case 'publishable':
        return svc.updateProjectStateToPublishable(projectId);
      case 'published':
        return svc.updateProjectStateToPublished(projectId);
      default:
        return null;
    }
  }

  private reloadProjectFromServer(): void {
    const id = this.selectedProjectId()?.trim();
    if (!id) {
      return;
    }
    this.projectService.getProjectAdminById(id).subscribe({
      next: (p) => {
        this.project.set(p as ProjectAdminResponse);
        setTimeout(() => this.syncAdminSettingsStepFromForm(), 0);
      },
      error: () => this.project.set(null),
    });
  }

  private saveAdminSettings(advanceAfterSave: boolean): void {
    const id = this.selectedProjectId()?.trim();
    const project = this.project();
    if (!id || !project) {
      this.toast.error(this.translate.instant('admin.toasts.dataLoadFailed'));
      return;
    }

    this.adminForm.markAllAsTouched();
    if (this.adminForm.invalid) {
      this.toast.error(this.translate.instant('validation.required'));
      return;
    }

    const body = this.buildAdminSaveBody(project, this.adminForm.getRawValue());
    this.savingAdmin.set(true);
    this.projectService
      .updateProjectAdmin(id, body)
      .pipe(
        switchMap(() => this.projectService.getProjectAdminById(id)),
        finalize(() => this.savingAdmin.set(false)),
      )
      .subscribe({
        next: (p) => {
          this.project.set(p as ProjectAdminResponse);
          this.toast.success(
            this.translate.instant('admin.toasts.savedSuccessfully'),
          );
          setTimeout(() => this.syncAdminSettingsStepFromForm(), 0);
          if (advanceAfterSave) {
            this.isAdminStepActive.set(false);
            this.stepManagerService.onStepClick(0);
          }
        },
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.saveFailed'));
        },
      });
  }

  private buildAdminSaveBody(
    project: ProjectAdminResponse,
    admin: ReturnType<FormGroup<AdminProjectIdeaFormGroup>['getRawValue']>,
  ): ProjectBaseRequest {
    return {
      contactUserId: admin.contactId ?? project.contactUserId ?? null,
      foundingMode: admin.foundingMode ?? project.foundingMode,
      loanTerm: admin.loanTerm ?? project.foundingMode ?? 0,
      classificationId:
        admin.investmentClassification ?? project.classification?.id ?? '',
      interestMin: project.interestMin ?? null,
      interestMax: project.interestMax ?? null,
      yieldMin: project.yieldMin ?? null,
      yieldMax: project.yieldMax ?? null,
      startCollectionDate:
        admin.startCollectionDate ?? project.startCollectionDate ?? null,
      endCollectionDate:
        admin.endCollectionDate ?? project.endCollectionDate ?? null,
      loanTypePaymentStart:
        admin.loanTypePaymentStart ?? project.loanTypePaymentStart ?? null,
      loanTypePaymentEnd:
        admin.loanTypePaymentEnd ?? project.loanTypePaymentEnd ?? null,
      name: project.name ?? null,
      description: project.description ?? null,
      country: project.country ?? null,
      city: project.city ?? null,
      currency: project.currency ?? null,
    };
  }
}
